/**
 * QuestItemIcons — Hollow Coil quest item icons (Coil Art Pack, Lane C).
 *
 * Design source of truth: docs/design/coil-art/ICON_DESIGN_SHEETS.md
 * Palette: docs/design/coil-art/COLOR_PALETTE.md — do not invent colors.
 *
 * Registered under the REAL content ids (content/items/materials.toml):
 *   keepers_journal_page · beach_trinket · ancient_relic · fresh_fish
 * plus `lantern_oil` (design-sheet id — no item exists yet; Maren's quest
 * currently collects slime_gel) and the design-sheet aliases
 * `journal_page` / `washed_trinket` so either naming resolves.
 *
 * Same conventions as MobDropIcons: 40×40 viewBox, gradients not flats,
 * no drop shadows, icon-prefixed gradient ids (`ci_*`).
 */

import { forestToolRoll } from './ForestQuestIcons';

function wrap40(size: number, content: string): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">${content}</svg>`;
}

/**
 * THE STORY ITEM — a yellowed page from the Hollow Keeper's journal.
 * Aged parchment, curled corner, faded writing: careful lines at the top,
 * hurried and trailing off at the bottom (per the item description —
 * "careful, then hurried, then something else entirely").
 */
function keepersJournalPage(size: number = 32): string {
  return wrap40(size, `
    <defs>
      <linearGradient id="ci_jp_page" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stop-color="#e8d8a0"/>
        <stop offset="100%" stop-color="#c8b880"/>
      </linearGradient>
    </defs>
    <!-- Page with worn, slightly wavy edges; bottom-right corner cut for the curl -->
    <path d="M10,5 Q16,3.8 22,4.5 Q27,4 30,5 L30.5,13 Q31,20 30,27 L24,35 L16,35.5 Q12,36 9.5,35 L9,26 Q8.5,17 9.2,10 Z"
      fill="url(#ci_jp_page)" stroke="#a89860" stroke-width="0.8"/>
    <!-- Shadow the curl casts on the page -->
    <path d="M30,27 L24,35 L22.6,33.2 Q26,29.5 30,27 Z" fill="#a89860" opacity="0.3"/>
    <!-- Curled corner flap -->
    <path d="M30,27 L24,35 Q24.6,30.4 27.2,28.6 Q28.6,27.6 30,27 Z" fill="#c8b880" stroke="#a89860" stroke-width="0.6"/>
    <!-- Faded ink: careful, then hurried, then trailing off -->
    <g stroke="#4a3a2a" stroke-width="1" opacity="0.6" fill="none" stroke-linecap="round">
      <path d="M13,10.5 Q19,10 26,10.5"/>
      <path d="M13,14.5 Q20,14 27,14.5"/>
      <path d="M13,18.5 Q18,18.2 25,18.6"/>
      <path d="M13,22.5 Q20,22 24.5,23"/>
      <path d="M13,26.8 L20,27.8"/>
    </g>
    <!-- A small ink blot where the hand faltered -->
    <circle cx="21.8" cy="27.6" r="0.8" fill="#4a3a2a" opacity="0.5"/>
  `);
}

/**
 * Beachcomber quest — a washed-up trinket. Shell variant per the design
 * sheet: sand-gold whorl smoothed by the sea, spiral line, gentle.
 */
function washedTrinket(size: number = 32): string {
  return wrap40(size, `
    <defs>
      <radialGradient id="ci_wt_shell" cx="40%" cy="35%" r="75%">
        <stop offset="0%" stop-color="#f5e3b0"/>
        <stop offset="100%" stop-color="#d4a060"/>
      </radialGradient>
    </defs>
    <!-- Opening lip (behind the whorl, bottom-right) -->
    <path d="M25,28 Q31,32 34.5,30.5 Q31.5,35.5 25,33.5 Q23,31 25,28 Z" fill="#d4a060"/>
    <!-- Shell whorl -->
    <circle cx="19" cy="19" r="13" fill="url(#ci_wt_shell)"/>
    <!-- Spiral (smoothed, sea-worn) -->
    <path d="M21,17.5 Q25.5,17 25,21.5 Q24.2,26.5 18.5,26 Q12,25.2 12,18.5 Q12,11.5 19.5,10.2 Q27.5,9 30.5,15.5"
      stroke="#d4a060" stroke-width="1.6" fill="none" stroke-linecap="round"/>
    <!-- Sun-bleached highlight -->
    <ellipse cx="14" cy="12.5" rx="3.5" ry="2" fill="rgba(255,255,255,0.4)" transform="rotate(-20 14 12.5)"/>
  `);
}

/**
 * Relic Collector — an old vault carving with a restrained amber pulse.
 * The uneven stone silhouette keeps it archaeological rather than magical
 * loot, while the central groove echoes the tomb's candle palette.
 */
function ancientRelic(size: number = 32): string {
  return wrap40(size, `
    <defs>
      <linearGradient id="ci_ar_stone" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stop-color="#8f887a"/>
        <stop offset="100%" stop-color="#57534d"/>
      </linearGradient>
      <radialGradient id="ci_ar_amber" cx="50%" cy="45%" r="65%">
        <stop offset="0%" stop-color="#ffd27a"/>
        <stop offset="100%" stop-color="#b96a2b"/>
      </radialGradient>
    </defs>
    <path d="M13,5 L27,6.5 L31.5,17 L28.5,33 L12,34.5 L8.5,20 Z"
      fill="url(#ci_ar_stone)" stroke="#3d3a36" stroke-width="1.2"/>
    <path d="M20,10 C14,13 14,21 20,29 C26,21 26,13 20,10 Z"
      fill="none" stroke="url(#ci_ar_amber)" stroke-width="2.2" stroke-linecap="round"/>
    <circle cx="20" cy="19.5" r="3.2" fill="url(#ci_ar_amber)" opacity="0.9"/>
    <path d="M11.5,18 L15,17 M25,24 L29,22 M14,29 L17,27" stroke="#b8ad97" stroke-width="0.8" opacity="0.55"/>
  `);
}

/**
 * Maren's quest — a small oil flask: pale translucent glass, amber
 * liquid, cork stopper. Warm.
 */
function lanternOil(size: number = 32): string {
  return wrap40(size, `
    <defs>
      <linearGradient id="ci_lo_oil" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stop-color="#ffcc00"/>
        <stop offset="100%" stop-color="#cc9900"/>
      </linearGradient>
    </defs>
    <!-- Cork stopper -->
    <rect x="16.5" y="3" width="7" height="5.5" rx="1.5" fill="#8b7355"/>
    <!-- Neck -->
    <rect x="17.5" y="7.5" width="5" height="5.5" fill="#e0e0e0" opacity="0.6"/>
    <!-- Round glass body -->
    <circle cx="20" cy="24" r="12" fill="#e0e0e0" opacity="0.5"/>
    <!-- Amber oil (fills the lower body, gentle meniscus) -->
    <path d="M8.9,19.5 A12,12 0 1 0 31.1,19.5 Q26,21.5 20,20.5 Q13,19.4 8.9,19.5 Z" fill="url(#ci_lo_oil)"/>
    <!-- Glass highlight -->
    <path d="M12.5,17.5 Q11,21 12,25.5" stroke="#ffffff" stroke-width="1.8" opacity="0.4" fill="none" stroke-linecap="round"/>
  `);
}

/** Fresh Catch quest — a silver fish. Simple and clean. */
function freshFish(size: number = 32): string {
  return wrap40(size, `
    <defs>
      <linearGradient id="ci_ff_body" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stop-color="#c0c0c0"/>
        <stop offset="100%" stop-color="#808080"/>
      </linearGradient>
    </defs>
    <!-- Dorsal fin -->
    <path d="M11,14 Q16,9 22,13.5 Q16.5,12.5 11,14 Z" fill="#a0a0a0"/>
    <!-- Tail -->
    <path d="M26,20 L35.5,13.5 Q33.5,20 35.5,26.5 Z" fill="#a0a0a0"/>
    <!-- Body -->
    <ellipse cx="16.5" cy="20" rx="11.5" ry="7.5" fill="url(#ci_ff_body)"/>
    <!-- Gill line -->
    <path d="M13,15.5 Q15,20 13,24.5" stroke="#808080" stroke-width="1" opacity="0.8" fill="none"/>
    <!-- Eye -->
    <circle cx="9.5" cy="18" r="1.4" fill="#1a1a1a"/>
    <circle cx="9.9" cy="17.6" r="0.4" fill="#ffffff" opacity="0.8"/>
    <!-- Highlight along the back -->
    <ellipse cx="14" cy="16" rx="5" ry="1.8" fill="rgba(255,255,255,0.4)" transform="rotate(-8 14 16)"/>
  `);
}

/** A living seed in a split fungal husk; deliberately different from a gem drop. */
function glowsporeHeartseed(size = 32): string {
  return wrap40(size, `<defs><radialGradient id="ci_heartseed" cx="35%" cy="30%" r="75%">
    <stop offset="0%" stop-color="#e4edb5"/><stop offset="100%" stop-color="#74bcaa"/>
    </radialGradient></defs>
    <path d="M20 4C8 10 9 26 20 33C32 26 33 10 20 4Z" fill="url(#ci_heartseed)" stroke="#47776b" stroke-width="1"/>
    <path d="M19 32Q4 27 6 12Q15 18 19 32Z" fill="#4d7160" stroke="#a7b58a" stroke-width=".8"/>
    <path d="M21 32Q36 26 34 14Q24 18 21 32Z" fill="#5d8270" stroke="#a7b58a" stroke-width=".8"/>
    <path d="M20 14V34M15 23L20 28L26 21M20 33L15 37M20 33L24 37" fill="none" stroke="#d1e8b7" stroke-width="1.2"/>
    <circle cx="18" cy="12" r="2" fill="#f0f2d0" opacity=".8"/>`);
}

export const QUEST_ITEM_ICONS: Record<string, (size?: number) => string> = {
  forest_tool_roll: forestToolRoll,
  moonfen_reedwater_tally: keepersJournalPage,
  moonfen_grange_ledger: keepersJournalPage,
  moonfen_last_watch_letter: keepersJournalPage,
  glowspore_field_notes: keepersJournalPage,
  glowspore_heartseed: glowsporeHeartseed,
  // Real content ids (content/items/materials.toml)
  keepers_journal_page: keepersJournalPage,
  beach_trinket: washedTrinket,
  ancient_relic: ancientRelic,
  fresh_fish: freshFish,
  // Design-sheet id — no item id exists yet (Maren's quest collects slime_gel);
  // registered so the icon is ready if/when the item lands.
  lantern_oil: lanternOil,
  // Design-sheet aliases (lane table names) — cheap insurance against id drift.
  journal_page: keepersJournalPage,
  washed_trinket: washedTrinket,
};
